/**
 * Phase 5 post-signoff — server-side templates for emails / consent docs / privacy
 * notices contain admin-authored HTML. We let the rich content render but defend
 * against XSS by routing every `dangerouslySetInnerHTML` payload through DOMPurify
 * with a conservative profile.
 *
 * The threat model is "compromised org_admin or seeded malicious template" — even
 * in that case, scripts/event-handlers/iframes etc. should not execute.
 */
import DOMPurify from "dompurify";

// Conservative profile: keep prose-friendly tags, drop everything risky.
const ALLOWED_TAGS = [
  "p", "br", "div", "span",
  "strong", "b", "em", "i", "u", "s",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li",
  "blockquote", "code", "pre",
  "a", "img",
  "table", "thead", "tbody", "tr", "th", "td",
  "hr",
];
const ALLOWED_ATTR = ["href", "title", "target", "rel", "src", "alt", "class", "style"];

const config = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  // Hard ban: scripts, event handlers, javascript:, data: (except images), iframes, forms.
  FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "button", "style", "meta", "link"],
  FORBID_ATTR: [
    "onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur",
    "onchange", "onsubmit", "onreset", "onkeydown", "onkeyup", "onkeypress",
  ],
  ALLOW_DATA_ATTR: false,
  KEEP_CONTENT: true,
  // Force all <a> to open with rel="noopener noreferrer" if target="_blank".
  ADD_ATTR: ["target", "rel"],
};

// Hook to harden anchors.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.hasAttribute("target")) {
    node.setAttribute("rel", "noopener noreferrer");
  }
  // Drop javascript: URLs that DOMPurify might somehow let through.
  if (node.hasAttribute("href")) {
    const href = node.getAttribute("href") || "";
    if (/^\s*javascript:/i.test(href)) node.removeAttribute("href");
  }
});

/**
 * Sanitize HTML for safe injection via dangerouslySetInnerHTML.
 *
 * Usage:
 *   <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(untrusted) }} />
 */
export function sanitizeHtml(html) {
  if (!html) return "";
  return DOMPurify.sanitize(String(html), config);
}

/**
 * Convenience helper that returns the React-ready prop object.
 *
 * Usage:
 *   <div {...safeHtmlProps(untrusted)} />
 */
export function safeHtmlProps(html) {
  return { dangerouslySetInnerHTML: { __html: sanitizeHtml(html) } };
}
